import mean from 'lodash/mean'
import sortBy from 'lodash/sortBy'
import uniq from 'lodash/uniq'
import {fromJust} from '@freckle/maybe'
import {parseQuestionText} from '@freckle/educator-entities/ts/common/helpers/string-helper'
import type {AssessmentAnswerT} from '@freckle/educator-entities/ts/math/assessment/models/assessment-answer'
import type {
  MathQuestionAttrs,
  MathQuestionMapT
} from '@freckle/educator-entities/ts/math/common/models/questions'

type AnswerWithStandard = AssessmentAnswerT & {standard: {name: string}}

export type QuestionGroups = {
  groups: {
    [x: string]: Array<AnswerWithStandard>
  }
  questionIdOrder: Array<string>
}

export function groupByQuestionId(
  assessmentAnswers: Array<AssessmentAnswerT>,
  standards: {id: string; name: string}[]
): QuestionGroups {
  const standardLookup = new Map(standards.map(s => [s.id, s]))
  const questionGroups: Record<string, AnswerWithStandard[]> = {}
  const questionIds: string[] = []

  const answersWithStandards = assessmentAnswers.flatMap(a => {
    const standard = standardLookup.get(a.standard.id)
    return standard === undefined ? [] : [{...a, standard}]
  })

  const sortedAnswers = sortBy(answersWithStandards, answer => answer.id)

  sortedAnswers.forEach(answer => {
    const questionId = answer.questionId

    const group = questionGroups[questionId]
    if (group === undefined) {
      questionGroups[questionId] = []
    }

    questionIds.push(questionId)

    questionGroups[questionId]!.unshift(answer)
  })

  return {
    groups: questionGroups,
    questionIdOrder: uniq(questionIds)
  }
}

export type QuestionSharedT = {
  questionIndex: number
  questionImageUrl: string | undefined | null
  questionText: string
  questionId: string
  standardCode: string
  questionCorrectAnswers: MathQuestionAttrs
  accuracy: number | undefined | null
  numQuestionAnswered: number
}

export function getSharedQuestions(
  questionGroups: QuestionGroups,
  questionMap: MathQuestionMapT
): Array<QuestionSharedT> {
  const sharedQuestions: Array<QuestionSharedT> = []
  const questionIds = questionGroups.questionIdOrder

  questionIds.forEach((questionId, questionIndex) => {
    // This is guaranteed to exist by the construction of questionGroups

    const asmtAnswerModels = questionGroups.groups[questionId]!

    const question = fromJust(
      questionMap.get(questionId),
      `Can't find question ${questionId} for assessment.`
    )

    // since all of these are for the same question, can just pluck the first one.

    const standardCode = asmtAnswerModels[0]!.standard.name
    const questionsAnswered = asmtAnswerModels.filter(
      ans => ans.correctness !== null && ans.correctness !== undefined
    )

    // If correctness is null, the student hasn't answered this question yet
    const accuracies = questionsAnswered.map(answer => answer.correctness)

    const accuracy = mean(accuracies)

    sharedQuestions.push({
      questionIndex: questionIndex + 1,
      questionImageUrl: question.questionDiagram?.url,
      questionText: parseQuestionText(question.questionText),
      questionId: question.id,
      standardCode,
      questionCorrectAnswers: question,
      // Coerce NaN to null, which happens when no students have answered the question yet (the mean
      // of an empty set of data is not a number)
      accuracy: isNaN(accuracy) ? null : accuracy,
      numQuestionAnswered: questionsAnswered.length
    })
  })

  return sharedQuestions
}
